import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { resetAdminPersistedJourneyState } from 'store/adminJourneyReset';

import type { OrgUnitRow } from 'utils/csvParser';

import type {
  PayrollValidationResult,
  PlanValidationResult,
} from 'pages/organizationFlow/orgUnitCsvApi';
import type { CsvValidatorDataRow } from 'pages/organizationFlow/type.data';

export type OrgOnboardingFlowType = 'create' | 'existing';
export type UnitCsvUploadStatus = 'idle' | 'incomplete' | 'failed' | 'success';

export interface OrgOnboardingState {
  flowType: OrgOnboardingFlowType;
  organizationName: string;
  units: string[];
  unitRows: OrgUnitRow[];
  validatorRows: CsvValidatorDataRow[];
  selectedUnit: string;
  payrollValidationResult: PayrollValidationResult | null;
  planValidationResult: PlanValidationResult | null;
  lastPayrollResultByUnit: Record<string, PayrollValidationResult>;
  unitCsvUploadStatus: UnitCsvUploadStatus;
  csvValidationStatus: string | null;
  selectedUnitWorkflowFromApi: {
    workflow_state?: string;
    payroll_id?: string;
  } | null;
  /** Last onboarding route the user was on (for resume after org-table toggle). */
  lastOnboardingPath: string | null;
  /** Highest step index the user may access in the forward-only flow (0 = home). */
  maxAuthorizedStepIndex: number;
}

const initialState: OrgOnboardingState = {
  flowType: 'existing',
  organizationName: '',
  units: [],
  unitRows: [],
  validatorRows: [],
  selectedUnit: '',
  payrollValidationResult: null,
  planValidationResult: null,
  lastPayrollResultByUnit: {},
  unitCsvUploadStatus: 'idle',
  csvValidationStatus: null,
  selectedUnitWorkflowFromApi: null,
  lastOnboardingPath: null,
  maxAuthorizedStepIndex: 0,
};

const orgOnboardingSlice = createSlice({
  name: 'orgOnboarding',
  initialState,
  reducers: {
    setFlowType(state, action: PayloadAction<OrgOnboardingFlowType>) {
      state.flowType = action.payload;
    },
    setOrganizationName(state, action: PayloadAction<string>) {
      state.organizationName = action.payload;
    },
    setUnits(state, action: PayloadAction<string[]>) {
      state.units = action.payload;
    },
    setUnitRows(state, action: PayloadAction<OrgUnitRow[]>) {
      state.unitRows = action.payload;
    },
    setValidatorRows(state, action: PayloadAction<CsvValidatorDataRow[]>) {
      state.validatorRows = action.payload;
    },
    setSelectedUnit(state, action: PayloadAction<string>) {
      state.selectedUnit = action.payload;
    },
    setPayrollValidationResult(state, action: PayloadAction<PayrollValidationResult | null>) {
      state.payrollValidationResult = action.payload;
    },
    setPlanValidationResult(state, action: PayloadAction<PlanValidationResult | null>) {
      state.planValidationResult = action.payload;
    },
    setLastPayrollResultForUnit(
      state,
      action: PayloadAction<{ unitId: string; result: PayrollValidationResult }>
    ) {
      state.lastPayrollResultByUnit[action.payload.unitId] = action.payload.result;
    },
    clearLastPayrollResultForUnit(state, action: PayloadAction<string>) {
      delete state.lastPayrollResultByUnit[action.payload];
    },
    setUnitCsvUploadStatus(state, action: PayloadAction<UnitCsvUploadStatus>) {
      state.unitCsvUploadStatus = action.payload;
    },
    setCsvValidationStatus(state, action: PayloadAction<string | null>) {
      state.csvValidationStatus = action.payload;
    },
    setSelectedUnitWorkflowFromApi(
      state,
      action: PayloadAction<OrgOnboardingState['selectedUnitWorkflowFromApi']>
    ) {
      state.selectedUnitWorkflowFromApi = action.payload;
    },
    setLastOnboardingPath(state, action: PayloadAction<string | null>) {
      state.lastOnboardingPath = action.payload;
    },
    setMaxAuthorizedStepIndex(state, action: PayloadAction<number>) {
      state.maxAuthorizedStepIndex = Math.max(0, action.payload);
    },
    resetOrgOnboardingJourney(state) {
      Object.assign(state, initialState);
    },
    hydrateOrgOnboarding(state, action: PayloadAction<Partial<OrgOnboardingState>>) {
      Object.assign(state, action.payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(resetAdminPersistedJourneyState, () => ({ ...initialState }));
  },
});

export const {
  setFlowType,
  setOrganizationName,
  setUnits,
  setUnitRows,
  setValidatorRows,
  setSelectedUnit,
  setPayrollValidationResult,
  setPlanValidationResult,
  setLastPayrollResultForUnit,
  clearLastPayrollResultForUnit,
  setUnitCsvUploadStatus,
  setCsvValidationStatus,
  setSelectedUnitWorkflowFromApi,
  setLastOnboardingPath,
  setMaxAuthorizedStepIndex,
  resetOrgOnboardingJourney,
  hydrateOrgOnboarding,
} = orgOnboardingSlice.actions;

export default orgOnboardingSlice.reducer;
